import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { defaultSnapshot } from './demo-data'
import type {
  AdminBannedUser, AdminPostRow, AdminReport, AdminReportStatus, AdminStats,
  AppVersion, AppyCreation, AppyDataAdapter, AppyNotification, AppySnapshot, AppyUser,
  DailySchedule, GenerationRequest, GenerationResult, Intelligence, ProfileUpdate,
  SnapshotLoadOptions, VersionRestoreResult,
} from './model'
import { isUuid, localRuntimeDelete, localRuntimeGet, localRuntimeSet } from './runtime-storage'

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined
const cloneDemo = (): AppySnapshot => JSON.parse(JSON.stringify(defaultSnapshot)) as AppySnapshot
const sleep = (ms: number) => new Promise<void>((resolve) => window.setTimeout(resolve, ms))

type Row = Record<string, unknown>

const PAGE_SIZE = 1000
const ID_CHUNK_SIZE = 100

const chunks = <T,>(items: T[], size = ID_CHUNK_SIZE) =>
  Array.from({ length: Math.ceil(items.length / size) }, (_, index) => items.slice(index * size, (index + 1) * size))

const queryError = (label: string, error: { message?: string } | null | undefined) => {
  if (error) throw new Error(`Could not load ${label}: ${error.message ?? 'server request failed'}`)
}

const uniqueRows = (rows: Row[]) => [...new Map(rows.map((row) => [String(row.id), row])).values()]

export const calendarMonthStart = (date = new Date()) =>
  new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1)).toISOString()

export const sumChargedUsage = (rows: Row[]) =>
  rows.reduce((total, row) => total + Number(row.charged_microusd ?? 0), 0) / 1_000_000

export const toAppVersion = (row: Row): AppVersion => ({
  id: String(row.id),
  number: Number(row.version_number),
  note: String(row.change_request ?? (Number(row.version_number) === 1 ? 'Initial build' : `Version ${Number(row.version_number)}`)),
  createdAt: String(row.created_at),
  srcDoc: String(row.source_html ?? ''),
  title: String(row.title ?? 'Untitled App'),
  description: String(row.summary ?? ''),
})

export const unknownAuthor = (id: string): AppyUser => ({
  id,
  name: 'Unknown creator',
  handle: `creator_${id.replace(/-/g, '').slice(0, 8) || 'unknown'}`,
  bio: 'This creator profile is unavailable.',
  avatar: '?',
  followers: 0,
  following: 0,
})

export const hasSupabaseConfig = Boolean(url && anonKey && !url.includes('YOUR_PROJECT'))

export const hasCompletedOnboarding = (user: Pick<AppyUser, 'name' | 'handle'>) => {
  const defaultName = /^(?:appy user|new maker|guest)$/i.test(user.name.trim())
  const defaultHandle = /^(?:appy|appy_[0-9a-f]{8}|maker_[0-9a-f]{6}|guest_[0-9a-f]{6})$/i.test(user.handle.trim())
  return !defaultName && !defaultHandle
}

/** Anonymous sessions are not real accounts — always present them as Guest. */
export const guestIdentity = (userId: string): Pick<AppyUser, 'name' | 'handle' | 'avatar' | 'bio'> => {
  const short = userId.replace(/-/g, '').slice(0, 6) || 'guest'
  return {
    name: 'Guest',
    handle: `guest_${short}`,
    avatar: 'G',
    bio: 'Browsing as a guest. Create an account to keep your apps, credits, and profile.',
  }
}

/** Profiles that should never appear in @ search / discovery. */
export const isGuestProfile = (user: Pick<AppyUser, 'name' | 'handle'>): boolean =>
  /^(?:guest|appy user|new maker|unknown creator)$/i.test(user.name.trim())
  || /^(?:guest_|appy_|maker_|creator_)/i.test(user.handle.trim())

export const isSocialPost = (app: Pick<AppyCreation, 'postType'> | { postType?: string | null }) =>
  app.postType === 'text' || app.postType === 'short'

/** Sample evenly spaced JPEG frames from a video for Luna moderation. */
export async function extractVideoFrames(
  file: File,
  count = 3,
  maxSeconds?: number,
): Promise<Array<{ base64: string; mediaType: string; durationSeconds: number }>> {
  const objectUrl = URL.createObjectURL(file)
  try {
    const video = document.createElement('video')
    video.muted = true
    video.playsInline = true
    video.preload = 'auto'
    video.src = objectUrl

    await new Promise<void>((resolve, reject) => {
      const onError = () => reject(new Error('Could not load that video.'))
      video.addEventListener('loadeddata', () => resolve(), { once: true })
      video.addEventListener('error', onError, { once: true })
    })

    const duration = Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 0
    if (maxSeconds && duration > maxSeconds + 0.5) {
      throw new Error(`Shorts on your plan can be up to ${maxSeconds} seconds.`)
    }

    const canvas = document.createElement('canvas')
    const width = Math.min(720, video.videoWidth || 720)
    const height = video.videoHeight
      ? Math.round((video.videoHeight / Math.max(1, video.videoWidth)) * width)
      : 1280
    canvas.width = width
    canvas.height = height
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not prepare video frames.')

    const points = count <= 1
      ? [0.15]
      : Array.from({ length: count }, (_, i) => 0.12 + (0.76 * i) / Math.max(1, count - 1))
    const frames: Array<{ base64: string; mediaType: string; durationSeconds: number }> = []

    for (const t of points) {
      const seekTo = duration > 0 ? Math.min(duration * t, Math.max(0, duration - 0.05)) : 0
      await new Promise<void>((resolve, reject) => {
        const onSeeked = () => resolve()
        const onError = () => reject(new Error('Could not sample that video.'))
        video.addEventListener('seeked', onSeeked, { once: true })
        video.addEventListener('error', onError, { once: true })
        try {
          video.currentTime = seekTo
        } catch {
          reject(new Error('Could not sample that video.'))
        }
      })
      ctx.drawImage(video, 0, 0, width, height)
      const dataUrl = canvas.toDataURL('image/jpeg', 0.72)
      const base64 = dataUrl.split(',')[1] || ''
      if (base64) frames.push({ base64, mediaType: 'image/jpeg', durationSeconds: Math.round(duration) || 1 })
    }
    return frames
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

export class SupabaseAdapter implements AppyDataAdapter {
  readonly mode = 'supabase' as const
  private readonly client: SupabaseClient
  private userId: string | null = null
  private publicProfile: Record<string, unknown> | null = null

  constructor() {
    if (!url || !anonKey) throw new Error('Supabase browser configuration is missing')
    this.client = createClient(url, anonKey, {
      auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true },
    })
  }

  private async ensureSession() {
    const { data } = await this.client.auth.getSession()
    if (data.session?.user) { this.userId = data.session.user.id; return data.session.user }
    const anonymous = await this.client.auth.signInAnonymously()
    if (anonymous.error || !anonymous.data.user) throw anonymous.error ?? new Error('Unable to start an Appy session')
    this.userId = anonymous.data.user.id
    return anonymous.data.user
  }

  private async loadProfiles(ids: string[]) {
    const rows: Row[] = []
    for (const group of chunks([...new Set(ids)].filter(Boolean))) {
      // Public view omits ban/staff secrets; load full row only for self when needed.
      const result = await this.client.from('public_profiles').select('*').in('id', group)
      if (result.error && /public_profiles|schema cache|does not exist/i.test(result.error.message ?? '')) {
        const fallback = await this.client.from('profiles')
          .select('id, handle, display_name, bio, avatar_url, banner_url, is_verified, follower_count, following_count, published_count, created_at')
          .in('id', group)
        queryError('creator profiles', fallback.error)
        rows.push(...((fallback.data ?? []) as Row[]))
      } else {
        queryError('creator profiles', result.error)
        rows.push(...((result.data ?? []) as Row[]))
      }
    }
    return uniqueRows(rows)
  }

  private async loadOwnProfile(userId: string) {
    const result = await this.client.from('profiles').select('*').eq('id', userId).maybeSingle()
    if (result.error) return null
    return result.data as Row | null
  }

  private async loadPosts(projectIds: string[]) {
    const rows: Row[] = []
    for (const group of chunks([...new Set(projectIds)].filter(Boolean))) {
      const result = await this.client.from('posts').select('*').in('project_id', group)
      queryError('posts', result.error)
      rows.push(...((result.data ?? []) as Row[]))
    }
    return uniqueRows(rows)
  }

  private async loadComments(postIds: string[]) {
    const rows: Row[] = []
    for (const group of chunks([...new Set(postIds)].filter(Boolean))) {
      for (let from = 0; ; from += PAGE_SIZE) {
        const result = await this.client.from('comments').select('*').in('post_id', group)
          .order('created_at', { ascending: false }).range(from, from + PAGE_SIZE - 1)
        queryError('comments', result.error)
        const page = (result.data ?? []) as Row[]
        rows.push(...page)
        if (page.length < PAGE_SIZE) break
      }
    }
    return uniqueRows(rows).sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))
  }

  private async loadProjectVersions(projectIds: string[]) {
    const rows: Row[] = []
    for (const group of chunks([...new Set(projectIds)].filter(Boolean))) {
      for (let from = 0; ; from += PAGE_SIZE) {
        const result = await this.client.from('project_versions')
          .select('id, project_id, version_number, title, summary, source_html, generation_prompt, change_request, created_at')
          .in('project_id', group).order('version_number', { ascending: true }).range(from, from + PAGE_SIZE - 1)
        queryError('version history', result.error)
        const page = (result.data ?? []) as Row[]
        rows.push(...page)
        if (page.length < PAGE_SIZE) break
      }
    }
    return uniqueRows(rows)
  }

  private async loadMonthlyUsage(userId: string) {
    const rows: Row[] = []
    for (let from = 0; ; from += PAGE_SIZE) {
      const result = await this.client.from('credit_usage').select('id, charged_microusd, created_at')
        .eq('user_id', userId).gte('created_at', calendarMonthStart())
        .order('created_at', { ascending: true }).order('id', { ascending: true })
        .range(from, from + PAGE_SIZE - 1)
      queryError('monthly usage', result.error)
      const page = (result.data ?? []) as Row[]
      rows.push(...page)
      if (page.length < PAGE_SIZE) break
    }
    return rows
  }

  async cancelBuild(projectId?: string): Promise<number> {
    const authUser = await this.ensureSession()
    if (authUser.is_anonymous) throw new Error('Sign in to stop a build.')
    const { data, error } = await this.client.rpc('cancel_my_active_builds', {
      p_reason: 'user_stop',
      p_project_id: projectId && isUuid(projectId) ? projectId : null,
    })
    if (error) throw error
    return Number(data ?? 0)
  }

  async loadSnapshot(options: SnapshotLoadOptions = {}): Promise<AppySnapshot | null> {
    const authUser = await this.ensureSession()
    // Only browser tab reloads should cancel builds. In-app Refresh must pass cancelActiveBuilds: false.
    if (!authUser.is_anonymous && options.cancelActiveBuilds) {
      try {
        await this.client.rpc('cancel_my_active_builds', { p_reason: 'client_refresh', p_project_id: null })
      } catch {
        // Older DBs without the RPC can still load the app.
      }
    }
    const [publicProjectsResult, ownProjectsResult, notificationsResult, subscriptionResult, walletResult, followsResult, likesResult, commentLikesResult, repostsResult, libraryResult, dailyScheduleResult, feedResult, draftsResult, monthlyUsageRows, topCreatorsResult, activeCreatorsResult, entitlementsResult] = await Promise.all([
      this.client.from('projects').select('*, current_version:project_versions!projects_current_version_fk(*)').eq('status', 'published').order('published_at', { ascending: false }).limit(100),
      this.client.from('projects').select('*, current_version:project_versions!projects_current_version_fk(*)').eq('owner_id', authUser.id).order('updated_at', { ascending: false }).limit(1000),
      this.client.from('notifications').select('*').order('created_at', { ascending: false }).limit(100),
      this.client.from('subscriptions').select('plan_key, status, current_period_end, updated_at').eq('user_id', authUser.id).in('status', ['trialing', 'active', 'past_due']).order('updated_at', { ascending: false }).limit(1).maybeSingle(),
      this.client.from('credit_wallets').select('*').eq('user_id', authUser.id).maybeSingle(),
      this.client.from('follows').select('*').eq('follower_id', authUser.id),
      this.client.from('post_likes').select('*').eq('user_id', authUser.id),
      this.client.from('comment_likes').select('comment_id').eq('user_id', authUser.id),
      this.client.from('reposts').select('*').eq('user_id', authUser.id),
      this.client.from('library_items').select('*').eq('user_id', authUser.id),
      this.client.from('daily_schedules').select('enabled, intelligence, local_hour').eq('user_id', authUser.id).maybeSingle(),
      this.client.rpc('get_for_you_feed', { p_limit: 100, p_offset: 0 }),
      this.client.from('project_drafts').select('project_id, version_id').eq('owner_id', authUser.id),
      this.loadMonthlyUsage(authUser.id),
      // Discovery candidates for Who to follow / Explore creators
      this.client.from('public_profiles').select('*').order('follower_count', { ascending: false }).limit(40),
      this.client.from('public_profiles').select('*').order('published_count', { ascending: false }).limit(40),
      this.client.rpc('my_credit_entitlements'),
    ])
    ;[
      ['public projects', publicProjectsResult], ['your projects', ownProjectsResult], ['notifications', notificationsResult],
      ['subscription', subscriptionResult], ['credit balance', walletResult], ['following', followsResult],
      ['likes', likesResult], ['comment likes', commentLikesResult], ['reposts', repostsResult],
      ['library', libraryResult], ['Daily Game schedule', dailyScheduleResult],
      ['project drafts', draftsResult],
    ].forEach(([label, result]) => queryError(String(label), (result as typeof publicProjectsResult).error))
    // Discovery + feed ranking are optional — never block the whole app if they fail.
    if (feedResult.error && !/could not find the function|PGRST202|permission|policy/i.test(feedResult.error.message ?? '')) {
      console.warn('For You feed unavailable', feedResult.error.message)
    }
    if (topCreatorsResult.error) console.warn('top creators unavailable', topCreatorsResult.error.message)
    if (activeCreatorsResult.error) console.warn('active creators unavailable', activeCreatorsResult.error.message)
    // Entitlements RPC is optional for older deploys; treat missing/failed as limited.
    if (entitlementsResult.error && !/could not find the function|PGRST202/i.test(entitlementsResult.error.message ?? '')) {
      queryError('credit entitlements', entitlementsResult.error)
    }

    let projectRows = uniqueRows([
      ...((ownProjectsResult.data ?? []) as Row[]),
      ...((publicProjectsResult.data ?? []) as Row[]),
    ])
    const linkedAppId = options.appId && isUuid(options.appId) ? options.appId : null
    if (linkedAppId && !projectRows.some((row) => String(row.id) === linkedAppId)) {
      const linkedResult = await this.client.from('projects')
        .select('*, current_version:project_versions!projects_current_version_fk(*)').eq('id', linkedAppId).maybeSingle()
      queryError('shared app', linkedResult.error)
      if (linkedResult.data) projectRows = uniqueRows([...projectRows, linkedResult.data as Row])
    }

    const projectIds = projectRows.map((row) => String(row.id))
    // Social posts: text + shorts (fallback to text-only if short kind not migrated yet)
    let socialPostsResult = await this.client.from('posts').select('*').in('post_kind', ['text', 'short']).order('published_at', { ascending: false }).limit(120)
    if (socialPostsResult.error && /short|post_kind|check|invalid/i.test(socialPostsResult.error.message ?? '')) {
      socialPostsResult = await this.client.from('posts').select('*').eq('post_kind', 'text').order('published_at', { ascending: false }).limit(100)
    }
    if (socialPostsResult.error && !/post_kind|column/i.test(socialPostsResult.error.message ?? '')) {
      queryError('social posts', socialPostsResult.error)
    }
    const textPosts = ((socialPostsResult.data ?? []) as Row[])
    const [posts, commentRows, versionRows, commentRepostRows] = await (async () => {
      const loadedPosts = await this.loadPosts(projectIds)
      const allPostIds = [...loadedPosts.map((row) => String(row.id)), ...textPosts.map((row) => String(row.id))]
      const [loadedComments, loadedVersions, loadedCommentReposts] = await Promise.all([
        this.loadComments(allPostIds),
        this.loadProjectVersions(projectIds),
        this.client.from('comment_reposts').select('comment_id').eq('user_id', authUser.id),
      ])
      if (loadedCommentReposts.error && !/comment_reposts|relation|does not exist/i.test(loadedCommentReposts.error.message ?? '')) {
        queryError('comment reposts', loadedCommentReposts.error)
      }
      return [loadedPosts, loadedComments, loadedVersions, ((loadedCommentReposts.data ?? []) as Row[])] as const
    })()

    const notificationRows = (notificationsResult.data ?? []) as Row[]
    const followed = new Set(((followsResult.data ?? []) as Row[]).map((row) => String(row.followed_id)))
    const profileIds = new Set<string>([authUser.id])
    projectRows.forEach((row) => profileIds.add(String(row.owner_id)))
    posts.forEach((row) => profileIds.add(String(row.author_id)))
    textPosts.forEach((row) => profileIds.add(String(row.author_id)))
    commentRows.forEach((row) => profileIds.add(String(row.author_id)))
    notificationRows.forEach((row) => { if (row.actor_id) profileIds.add(String(row.actor_id)) })
    followed.forEach((id) => profileIds.add(id))
    const linkedProfileId = options.profileId && isUuid(options.profileId) ? options.profileId : null
    if (linkedProfileId) profileIds.add(linkedProfileId)
    ;[
      ...((topCreatorsResult.data ?? []) as Row[]),
      ...((activeCreatorsResult.data ?? []) as Row[]),
    ].forEach((row) => profileIds.add(String(row.id)))
    let profileRows = uniqueRows([
      ...((topCreatorsResult.data ?? []) as Row[]),
      ...((activeCreatorsResult.data ?? []) as Row[]),
      ...(await this.loadProfiles([...profileIds])),
    ])
    if (!profileRows.some((row) => String(row.id) === authUser.id)) {
      await sleep(500)
      profileRows = uniqueRows([...profileRows, ...(await this.loadProfiles([authUser.id]))])
    }
    // Own/staff full profile for privileges (staff_role, ban status on self).
    // uniqueRows keeps the *last* row per id — own full profile must win over public_profiles.
    const ownFull = !authUser.is_anonymous ? await this.loadOwnProfile(authUser.id) : null
    if (ownFull) {
      profileRows = uniqueRows([...profileRows, ownFull])
    }
    // Reliable staff role via security-definer RPC (also re-syncs platform owner).
    let myStaffRole: AppyUser['staffRole'] = null
    if (!authUser.is_anonymous) {
      const staffResult = await this.client.rpc('my_staff_role')
      if (!staffResult.error && (staffResult.data === 'owner' || staffResult.data === 'moderator')) {
        myStaffRole = staffResult.data
      } else if (ownFull?.staff_role === 'owner' || ownFull?.staff_role === 'moderator') {
        myStaffRole = ownFull.staff_role as AppyUser['staffRole']
      }
    }

    const isAnonymous = Boolean(authUser.is_anonymous)
    let users: AppyUser[] = profileRows.map((row) => {
      const isSelf = String(row.id) === authUser.id
      const roleFromRow = row.staff_role === 'owner' || row.staff_role === 'moderator'
        ? row.staff_role as AppyUser['staffRole']
        : null
      return {
        id: String(row.id), name: String(row.display_name ?? 'Appy user'), handle: String(row.handle ?? 'appy'),
        bio: String(row.bio ?? ''), avatar: String(row.display_name ?? 'A').slice(0, 2).toUpperCase(),
        avatarUrl: row.avatar_url ? String(row.avatar_url) : null,
        bannerUrl: row.banner_url ? String(row.banner_url) : null,
        followers: Number(row.follower_count ?? 0), following: Number(row.following_count ?? 0),
        verified: Boolean(row.is_verified),
        staffRole: isSelf ? (myStaffRole ?? roleFromRow) : roleFromRow,
        verificationStatus: (['none', 'pending', 'verified', 'rejected'].includes(String(row.verification_status ?? (row.is_verified ? 'verified' : 'none')))
          ? String(row.verification_status ?? (row.is_verified ? 'verified' : 'none'))
          : 'none') as AppyUser['verificationStatus'],
        banned: Boolean(row.banned_at),
        banReason: row.ban_reason ? String(row.ban_reason) : null,
        followedByMe: followed.has(String(row.id)),
      }
    })
    // Drop leftover guest shells from discovery / @ search (current session kept separately).
    users = users.filter((user) => user.id === authUser.id || !isGuestProfile(user))
    if (!users.some((item) => item.id === authUser.id)) {
      users.unshift(isAnonymous
        ? { id: authUser.id, ...guestIdentity(authUser.id), followers: 0, following: 0 }
        : {
          id: authUser.id, name: 'New maker', handle: `maker_${authUser.id.slice(0, 6)}`, bio: 'Building on Appy.', avatar: 'NM',
          followers: 0, following: 0, staffRole: myStaffRole, verified: myStaffRole === 'owner',
        })
    }
    // Apply staff role on self even if profile row was incomplete.
    if (myStaffRole) {
      users = users.map((user) => user.id === authUser.id
        ? { ...user, staffRole: myStaffRole, verified: user.verified || myStaffRole === 'owner' }
        : user)
    }
    // Never show leftover guest-session nicknames (e.g. "Sora") as if the user signed in.
    if (isAnonymous) {
      const guest = guestIdentity(authUser.id)
      users = users.map((user) => user.id === authUser.id ? { ...user, ...guest, verified: false, staffRole: null } : user)
    }
    profileIds.forEach((id) => {
      if (users.some((user) => user.id === id)) return
      // Skip fabricating unknown guest-like ids into the people graph.
      if (id !== authUser.id) users.push(unknownAuthor(id))
    })

    const postByProject = new Map(posts.map((row) => [String(row.project_id), row]))
    const versionsByProject = new Map<string, Row[]>()
    versionRows.forEach((row) => {
      const id = String(row.project_id)
      versionsByProject.set(id, [...(versionsByProject.get(id) ?? []), row])
    })
    const draftByProject = new Map(((draftsResult.data ?? []) as Row[]).map((row) => [String(row.project_id), String(row.version_id)]))
    const liked = new Set(((likesResult.data ?? []) as Row[]).map((row) => String(row.post_id)))
    const reposted = new Set(((repostsResult.data ?? []) as Row[]).map((row) => String(row.post_id)))
    const commentReposted = new Set(commentRepostRows.map((row) => String(row.comment_id)))
    const saved = new Set(((libraryResult.data ?? []) as Row[]).map((row) => String(row.project_id)))
    const feedOrder = new Map(((feedResult.data ?? []) as Row[]).map((row, index) => [String(row.project_id), index]))
    const projectApps: AppyCreation[] = projectRows.map((row) => {
      const projectId = String(row.id)
      const joinedVersionRaw = row.current_version
      const joinedVersion = (Array.isArray(joinedVersionRaw) ? joinedVersionRaw[0] : joinedVersionRaw) as Row | null
      const historyRows = versionsByProject.get(projectId) ?? []
      const draftId = String(row.owner_id) === authUser.id ? draftByProject.get(projectId) : undefined
      const version = (draftId ? historyRows.find((item) => String(item.id) === draftId) : null) ?? joinedVersion
      const versions = historyRows.map(toAppVersion).sort((a, b) => a.number - b.number)
      if (version && !versions.some((item) => item.id === String(version.id))) versions.push(toAppVersion(version))
      const post = postByProject.get(projectId)
      const source = String(version?.source_html ?? '')
      return {
        id: projectId, postId: post ? String(post.id) : undefined, postType: 'app' as const, authorId: String(row.owner_id),
        title: String(version?.title ?? row.title ?? 'Untitled App'), description: String(version?.summary ?? row.summary ?? ''),
        postBody: post ? String(post.body ?? '') : undefined,
        prompt: String(version?.generation_prompt ?? ''), kind: (String(row.kind) === 'game' ? 'game' : 'app') as AppyCreation['kind'], status: String(row.status) as AppyCreation['status'],
        createdAt: String(row.created_at), updatedAt: String(row.updated_at), publishedAt: row.published_at ? String(row.published_at) : undefined,
        srcDoc: source, coverClass: String(row.kind) === 'game' ? 'cover-orbit' : 'cover-ink',
        coverUrl: row.cover_url ? String(row.cover_url) : null,
        tags: Array.isArray(row.tags) ? row.tags.map(String) : [],
        likes: Number(post?.like_count ?? row.like_count ?? 0), comments: Number(post?.comment_count ?? row.comment_count ?? 0),
        reposts: Number(post?.repost_count ?? row.repost_count ?? 0), saves: Number(post?.bookmark_count ?? 0), plays: Number(row.play_count ?? 0),
        likedByMe: post ? liked.has(String(post.id)) : false, savedByMe: saved.has(projectId), repostedByMe: post ? reposted.has(String(post.id)) : false,
        version: Number(version?.version_number ?? 0), versions,
      }
    })
    const textApps: AppyCreation[] = textPosts.map((row) => {
      const postId = String(row.id)
      const body = String(row.body ?? '')
      const hashtags = Array.from(body.matchAll(/#([a-zA-Z][\w]{1,29})/g)).map((match) => match[1])
      const kind = String(row.post_kind ?? 'text') === 'short' ? 'short' as const : 'text' as const
      const videoUrl = row.video_url ? String(row.video_url) : (kind === 'short' && row.media_url ? String(row.media_url) : null)
      const mediaUrl = row.media_url && kind !== 'short' ? String(row.media_url) : null
      const mediaKind = (row.media_kind === 'image' || row.media_kind === 'video')
        ? row.media_kind as 'image' | 'video'
        : videoUrl ? 'video' as const : mediaUrl ? 'image' as const : 'none' as const
      return {
        id: postId, postId, postType: kind, authorId: String(row.author_id),
        title: kind === 'short' ? 'Short' : 'Post', description: body, postBody: body,
        prompt: '', kind: 'app' as const, status: 'published' as const,
        createdAt: String(row.created_at ?? row.published_at), updatedAt: String(row.updated_at ?? row.published_at),
        publishedAt: String(row.published_at ?? row.created_at),
        srcDoc: '', coverClass: kind === 'short' ? 'cover-short' : 'cover-text',
        mediaUrl, videoUrl, mediaKind,
        durationSeconds: row.duration_seconds != null ? Number(row.duration_seconds) : null,
        tags: hashtags.length > 0 ? hashtags : (kind === 'short' ? ['Short'] : ['Post']),
        likes: Number(row.like_count ?? 0), comments: Number(row.comment_count ?? 0),
        reposts: Number(row.repost_count ?? 0), saves: Number(row.bookmark_count ?? 0), plays: Number(row.play_count ?? 0),
        likedByMe: liked.has(postId), savedByMe: false, repostedByMe: reposted.has(postId),
        version: 1, versions: [],
      }
    })
    const remoteApps: AppyCreation[] = [...projectApps, ...textApps].sort((a, b) => {
      const aRank = feedOrder.get(a.id)
      const bRank = feedOrder.get(b.id)
      if (aRank !== undefined && bRank !== undefined) return aRank - bRank
      if (aRank !== undefined) return -1
      if (bRank !== undefined) return 1
      return Date.parse(b.publishedAt ?? b.updatedAt) - Date.parse(a.publishedAt ?? a.updatedAt)
    })
    const apps = remoteApps
    const commentLiked = new Set(((commentLikesResult.data ?? []) as Row[]).map((row) => String(row.comment_id)))
    const appIdByPost = new Map<string, string>([
      ...posts.map((row) => [String(row.id), String(row.project_id)] as const),
      ...textPosts.map((row) => [String(row.id), String(row.id)] as const),
    ])
    const notifications: AppyNotification[] = notificationRows.map((row) => {
      const kind = String(row.kind)
      const type: AppyNotification['type'] =
        kind === 'generation_complete' || kind === 'generation_failed' || kind === 'daily_posted' ? 'build'
          : kind === 'verification' ? 'verification'
            : kind === 'billing' || kind === 'credits_low' ? 'system'
              : kind === 'mention' ? 'mention'
                : kind === 'reply' ? 'reply'
                  : (['like', 'comment', 'repost', 'follow'].includes(kind) ? kind as AppyNotification['type'] : 'system')
      const projectId = row.project_id ? String(row.project_id) : undefined
      const postId = row.post_id ? String(row.post_id) : undefined
      // Text posts use post id as app id; app posts use project id.
      const appId = projectId ?? (postId && textPosts.some((tp) => String(tp.id) === postId) ? postId : projectId)
      return {
        id: String(row.id), type,
        title: String(row.title), body: String(row.body ?? ''), createdAt: String(row.created_at), read: Boolean(row.read_at),
        appId, postId, userId: row.actor_id ? String(row.actor_id) : undefined,
      }
    })
    const me = users.find((item) => item.id === authUser.id)!
    const meRow = profileRows.find((row) => String(row.id) === authUser.id)
    this.publicProfile = isAnonymous || (meRow && Boolean(meRow.is_private)) ? null : {
      id: me.id, handle: me.handle, display_name: me.name, avatar_url: meRow?.avatar_url ?? null,
      bio: me.bio, is_verified: Boolean(me.verified),
    }
    const wallet = walletResult.data as Row | null
    const subscription = subscriptionResult.data as Row | null
    const plan = subscription?.plan_key === 'pro' ? 'studio' : subscription?.plan_key === 'creator' ? 'creator' : 'starter'
    const creditAllowance = plan === 'studio' ? 24 : plan === 'creator' ? 18 : 0
    const dailySchedule = dailyScheduleResult.data as Row | null
    const accountStatus: AppySnapshot['account']['status'] = isAnonymous
      ? 'anonymous'
      : authUser.email_confirmed_at
        ? 'secured'
        : 'verification-pending'
    let mfaEnabled = false
    if (!isAnonymous) {
      try {
        const factors = await this.client.auth.mfa.listFactors()
        mfaEnabled = (factors.data?.totp ?? []).some((factor) => factor.status === 'verified')
      } catch {
        mfaEnabled = false
      }
    }
    return {
      currentUserId: authUser.id, users, apps,
      comments: commentRows.map((row) => {
        const commentId = String(row.id)
        return {
          id: commentId,
          appId: appIdByPost.get(String(row.post_id)) ?? '',
          userId: String(row.author_id),
          body: String(row.body),
          createdAt: String(row.created_at),
          likes: Number(row.like_count ?? 0),
          likedByMe: commentLiked.has(commentId),
          parentId: row.parent_id ? String(row.parent_id) : null,
          replyCount: Number(row.reply_count ?? 0),
          reposts: Number(row.repost_count ?? 0),
          repostedByMe: commentReposted.has(commentId),
        }
      }),
      notifications,
      settings: {
        ...cloneDemo().settings,
        intelligence: (meRow?.intelligence as Intelligence | undefined) ?? 'medium',
        dailyGame: Boolean(dailySchedule?.enabled),
        dailyGameHour: Number.isInteger(Number(dailySchedule?.local_hour)) ? Math.min(23, Math.max(0, Number(dailySchedule?.local_hour))) : 9,
        dailyGameIntelligence: (['low', 'medium', 'high'].includes(String(dailySchedule?.intelligence)) ? String(dailySchedule?.intelligence) : 'medium') as Intelligence,
        profileVisibility: meRow && Boolean(meRow.is_private) ? 'private' : 'public',
      },
      billing: {
        plan,
        creditBalance: Number(wallet?.balance_microusd ?? 0) / 1_000_000,
        creditAllowance,
        renewsAt: subscription?.current_period_end ? String(subscription.current_period_end) : new Date(Date.now() + 30 * 86_400_000).toISOString(),
        usageThisMonth: sumChargedUsage(monthlyUsageRows),
        unlimited: Boolean((entitlementsResult.data as { unlimited?: boolean } | null)?.unlimited),
      },
      account: { status: accountStatus, email: authUser.email, mfaEnabled },
      // Guests skip the "create profile" screen — Create account is the real signup path.
      onboarded: isAnonymous ? true : hasCompletedOnboarding(me),
    }
  }

  async saveSnapshot(snapshot: AppySnapshot): Promise<void> {
    if (!this.userId) return
    const me = snapshot.users.find((item) => item.id === snapshot.currentUserId)
    if (!me) return
    const { data: auth } = await this.client.auth.getUser()
    if (auth.user?.is_anonymous) {
      const guest = guestIdentity(this.userId)
      const { error } = await this.client.from('profiles').update({
        display_name: guest.name,
        handle: guest.handle,
        bio: guest.bio,
        intelligence: snapshot.settings.intelligence,
        is_private: snapshot.settings.profileVisibility === 'private',
      }).eq('id', this.userId)
      if (error) throw error
      return
    }
    const { error } = await this.client.from('profiles').update({ display_name: me.name, handle: me.handle, bio: me.bio, intelligence: snapshot.settings.intelligence, is_private: snapshot.settings.profileVisibility === 'private' }).eq('id', this.userId)
    if (error) throw error
  }

  async updateProfile(profile: ProfileUpdate): Promise<void> {
    const authUser = await this.ensureSession()
    if (authUser.is_anonymous) throw new Error('Create an Appy account before setting a public profile.')
    // Prefer locked-down RPC (ignores staff/follower fields). Fall back to column-limited own-row update.
    const { error: rpcError } = await this.client.rpc('update_my_profile', {
      p_display_name: profile.name,
      p_handle: profile.handle,
      p_bio: profile.bio ?? '',
      p_avatar_url: profile.avatarUrl === undefined ? null : profile.avatarUrl,
      p_banner_url: profile.bannerUrl === undefined ? null : profile.bannerUrl,
    })
    if (rpcError) {
      if (!/update_my_profile|function|schema cache|does not exist/i.test(rpcError.message ?? '')) {
        throw rpcError
      }
      const patch: Record<string, unknown> = {
        display_name: profile.name,
        handle: profile.handle,
        bio: profile.bio,
      }
      if (profile.avatarUrl !== undefined) patch.avatar_url = profile.avatarUrl
      if (profile.bannerUrl !== undefined) patch.banner_url = profile.bannerUrl
      const { error } = await this.client.from('profiles').update(patch).eq('id', authUser.id)
      if (error) throw error
    }
    if (this.publicProfile) {
      this.publicProfile = {
        ...this.publicProfile,
        display_name: profile.name,
        handle: profile.handle,
        bio: profile.bio,
        avatar_url: profile.avatarUrl !== undefined ? profile.avatarUrl : this.publicProfile.avatar_url,
      }
    }
  }

  async setProfileVisibility(visibility: AppySnapshot['settings']['profileVisibility']): Promise<void> {
    const user = this.userId ? { id: this.userId } : await this.ensureSession()
    const { error: rpcError } = await this.client.rpc('set_my_profile_visibility', {
      p_private: visibility === 'private',
    })
    if (rpcError && !/set_my_profile_visibility|function|schema cache|does not exist/i.test(rpcError.message ?? '')) {
      throw rpcError
    }
    if (rpcError) {
      const { data, error } = await this.client.from('profiles').update({ is_private: visibility === 'private' }).eq('id', user.id).select('id, handle, display_name, avatar_url, bio, is_verified').single()
      if (error) throw error
      this.publicProfile = visibility === 'private' ? null : data as Record<string, unknown>
      return
    }
    if (visibility === 'private') {
      this.publicProfile = null
    } else {
      const { data } = await this.client.from('profiles').select('id, handle, display_name, avatar_url, bio, is_verified').eq('id', user.id).maybeSingle()
      this.publicProfile = (data as Record<string, unknown> | null) ?? this.publicProfile
    }
  }

  private extractErrorMessage(payload: unknown): string | null {
    if (!payload) return null
    if (typeof payload === 'string' && payload.trim()) return payload.trim()
    if (typeof payload !== 'object') return null
    const row = payload as { error?: unknown; message?: unknown; msg?: unknown }
    if (typeof row.error === 'string' && row.error.trim()) return row.error.trim()
    if (row.error && typeof row.error === 'object') {
      const nested = row.error as { message?: unknown; code?: unknown }
      if (typeof nested.message === 'string' && nested.message.trim()) return nested.message.trim()
    }
    if (typeof row.message === 'string' && row.message.trim()) return row.message.trim()
    if (typeof row.msg === 'string' && row.msg.trim()) return row.msg.trim()
    return null
  }

  private async functionError(
    result: { error?: unknown; data?: unknown; response?: Response },
    fallback: string,
  ): Promise<Error> {
    const fromData = this.extractErrorMessage(result.data)
    if (fromData) return new Error(fromData)

    const bodies: unknown[] = []
    const pushResponseBody = async (value: unknown) => {
      if (!(value instanceof Response)) return
      try {
        bodies.push(await value.clone().json())
      } catch {
        try {
          const text = await value.clone().text()
          if (text.trim()) bodies.push(text)
        } catch { /* ignore unreadable bodies */ }
      }
    }
    await pushResponseBody(result.response)
    const errObj = result.error as { context?: unknown } | null | undefined
    if (errObj?.context) await pushResponseBody(errObj.context)
    for (const body of bodies) {
      const message = this.extractErrorMessage(body)
      if (message) return new Error(message)
    }

    if (result.error instanceof Error && result.error.message) {
      if (!/non-2xx|edge function/i.test(result.error.message)) return result.error
    }
    if (result.error && typeof result.error === 'object' && 'message' in result.error) {
      const message = String((result.error as { message?: unknown }).message ?? '').trim()
      if (message && !/non-2xx|edge function/i.test(message)) return new Error(message)
    }
    return new Error(fallback)
  }

  private async ensureFreshSession() {
    const current = await this.client.auth.getSession()
    if (!current.data.session) {
      throw new Error('Your session expired. Sign out and sign back in, then try the build again.')
    }
    // Refresh proactively so long builder sessions do not fail the first invoke with invalid_session.
    const refreshed = await this.client.auth.refreshSession()
    if (refreshed.error || !refreshed.data.session) {
      // Keep using the existing session if refresh is not required yet.
      if (current.data.session.expires_at && current.data.session.expires_at * 1000 > Date.now() + 30_000) {
        return current.data.session
      }
      throw new Error('Your session expired. Sign out and sign back in, then try the build again.')
    }
    return refreshed.data.session
  }

  private async startAndPoll(body: Record<string, unknown>, onProgress?: GenerationRequest['onProgress']): Promise<GenerationResult> {
    await this.ensureFreshSession()
    const started = await this.client.functions.invoke('generate-app', { body })
    if (started.error) throw await this.functionError(started, 'Could not start the build.')
    const jobId = String((started.data as { job_id?: unknown } | null)?.job_id ?? '')
    if (!jobId) throw await this.functionError(started, 'Generation job did not start.')
    const startedProject = String((started.data as { project_id?: unknown } | null)?.project_id ?? '')
    if (typeof body.project_id === 'string' && startedProject && startedProject !== body.project_id) {
      throw new Error('The build was created with an unexpected project ID')
    }
    onProgress?.({ message: 'Opening model session…', status: 'queued', at: new Date().toISOString(), file: 'session' })
    const seen = new Set<string>()
    let lastMessage = ''
    for (let attempt = 0; attempt < 120; attempt += 1) {
      await sleep(attempt < 4 ? 1200 : 2800)
      const polled = await this.client.functions.invoke('poll-generation', { body: { job_id: jobId } })
      if (polled.error) throw await this.functionError(polled, 'Could not check build progress.')
      const progressRows = Array.isArray(polled.data?.progress)
        ? polled.data.progress as { message?: string; at?: string; status?: string; file?: string }[]
        : []
      for (const row of progressRows) {
        const key = `${row.at ?? ''}:${row.message ?? ''}`
        if (!row.message || seen.has(key)) continue
        seen.add(key)
        lastMessage = String(row.message)
        onProgress?.({
          message: lastMessage,
          at: row.at ? String(row.at) : undefined,
          status: row.status ? String(row.status) : undefined,
          file: row.file ? String(row.file) : undefined,
        })
      }
      if (typeof polled.data?.message === 'string' && polled.data.message !== lastMessage && !seen.has(polled.data.message)) {
        seen.add(polled.data.message)
        lastMessage = polled.data.message
        onProgress?.({
          message: lastMessage,
          status: String(polled.data.provider_status ?? polled.data.status ?? 'building'),
          at: new Date().toISOString(),
          file: polled.data.file ? String(polled.data.file) : undefined,
        })
      }
      if (polled.data?.status === 'failed') {
        const code = String(polled.data.error_code ?? '')
        if (code === 'user_stop' || code === 'client_refresh' || /stopped|cancelled/i.test(String(polled.data.error ?? ''))) {
          throw new Error(String(polled.data.error ?? 'Build stopped.'))
        }
        throw new Error(String(polled.data.error ?? polled.data.message ?? 'The build failed'))
      }
      if (polled.data?.status === 'ready' || polled.data?.status === 'published' || polled.data?.status === 'completed') {
        const srcDoc = String(polled.data.source_html ?? '')
        if (!srcDoc.trim()) throw new Error('The build finished but no app code was returned. Try again.')
        onProgress?.({ message: 'Build finished — validating HTML and saving a version…', status: 'completed', at: new Date().toISOString() })
        return {
          title: String(polled.data.title ?? 'New App'), description: String(polled.data.description ?? polled.data.summary ?? ''),
          srcDoc, tags: Array.isArray(polled.data.tags) ? polled.data.tags.map(String) : ['Made with Appy'],
          estimatedCost: Number(polled.data.charged_microusd ?? 0) / 1_000_000,
        }
      }
    }
    throw new Error('The build is still running. It remains saved in Builds.')
  }

  generate(request: GenerationRequest) {
    return this.startAndPoll({ project_id: request.projectId, prompt: request.prompt, kind: request.kind, intelligence: request.intelligence, title: request.title }, request.onProgress)
  }
  revise(app: AppyCreation, instruction: string, intelligence: Intelligence, onProgress?: GenerationRequest['onProgress']) {
    if (!isUuid(app.id)) return Promise.reject(new Error('Only your Appy builds can be edited'))
    const changeRequest = instruction.trim()
    if (changeRequest.length < 2) return Promise.reject(new Error('Describe the specific change you want to make.'))
    const prompt = (app.prompt || app.title || app.description || 'Appy app').trim()
    return this.startAndPoll({
      project_id: app.id,
      prompt,
      change_request: changeRequest,
      kind: app.kind,
      intelligence,
      title: app.title,
    }, onProgress)
  }
  async toggleLike(app: AppyCreation) { if (app.postId) { const { error } = await this.client.rpc('toggle_post_like', { p_post_id: app.postId }); if (error) throw error } }
  async toggleSave(app: AppyCreation) {
    if (isSocialPost(app)) return
    if (isUuid(app.id)) { const { error } = await this.client.rpc('toggle_library_item', { p_project_id: app.id, p_version_id: null }); if (error) throw error }
  }
  async toggleRepost(app: AppyCreation) { if (app.postId) { const { error } = await this.client.rpc('toggle_repost', { p_post_id: app.postId, p_comment: null }); if (error) throw error } }
  async toggleFollow(userId: string) { if (isUuid(userId)) { const { error } = await this.client.rpc('toggle_follow', { p_followed_id: userId }); if (error) throw error } }
  async addComment(app: AppyCreation, body: string, parentId?: string | null) {
    const user = await this.ensureSession()
    if (user.is_anonymous) throw new Error('Create an account to join the conversation.')
    if (!app.postId || !isUuid(app.postId)) throw new Error('This post is not open for comments yet.')
    const caption = body.trim()
    if (!caption) throw new Error('Write a message first.')
    // Only attach a parent when it is a real server id (not an optimistic local id that never landed).
    const parent = parentId && isUuid(parentId) ? parentId : null
    const { data, error } = await this.client.rpc('add_comment', {
      p_post_id: app.postId,
      p_body: caption,
      p_parent_id: parent,
    })
    if (error) {
      // Fallback for environments that have not pulled the RPC yet.
      if (/function|does not exist|schema cache/i.test(error.message ?? '')) {
        const payload: Row = { post_id: app.postId, author_id: user.id, body: caption }
        if (parent) payload.parent_id = parent
        const { error: insertError } = await this.client.from('comments').insert(payload)
        if (insertError) throw insertError
        return
      }
      throw error
    }
    return typeof data === 'string' ? data : undefined
  }

  async recordImpression(postId: string, dwellMs: number, surface: string = 'for_you', opened = false) {
    if (!isUuid(postId) || dwellMs < 250) return
    const user = await this.ensureSession()
    if (user.is_anonymous) return
    const { error } = await this.client.rpc('record_post_impression', {
      p_post_id: postId,
      p_dwell_ms: Math.round(dwellMs),
      p_surface: surface,
      p_opened: opened,
    })
    if (error && !/function|does not exist|schema cache/i.test(error.message ?? '')) {
      // Non-fatal ranking signal
      console.warn('record_post_impression', error.message)
    }
  }

  async loadFeedSignals(): Promise<{
    followedIds: string[]
    likedAuthors: Record<string, number>
    commentedAuthors: Record<string, number>
    dwellByPost: Record<string, number>
  }> {
    const empty = { followedIds: [] as string[], likedAuthors: {}, commentedAuthors: {}, dwellByPost: {} }
    const user = await this.ensureSession()
    if (user.is_anonymous) return empty
    const { data, error } = await this.client.rpc('my_feed_signals')
    if (error || !data || typeof data !== 'object') return empty
    const row = data as Row
    const followed = Array.isArray(row.followed_ids) ? row.followed_ids.map(String) : []
    const asNumberMap = (value: unknown): Record<string, number> => {
      if (!value || typeof value !== 'object') return {}
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>).map(([key, count]) => [key, Number(count) || 0]),
      )
    }
    return {
      followedIds: followed,
      likedAuthors: asNumberMap(row.liked_authors),
      commentedAuthors: asNumberMap(row.commented_authors),
      dwellByPost: asNumberMap(row.dwell_by_post),
    }
  }
  async toggleCommentRepost(commentId: string, _reposted: boolean) {
    if (!isUuid(commentId)) throw new Error('Invalid comment')
    const { error } = await this.client.rpc('toggle_comment_repost', { p_comment_id: commentId })
    if (error) {
      // Fallback if RPC not yet migrated: direct toggle
      const user = await this.ensureSession()
      if (_reposted) {
        const { error: delError } = await this.client.from('comment_reposts').delete().eq('user_id', user.id).eq('comment_id', commentId)
        if (delError) throw delError
      } else {
        const { error: insError } = await this.client.from('comment_reposts').insert({ user_id: user.id, comment_id: commentId })
        if (insError) throw insError
      }
    }
  }
  async toggleCommentLike(commentId: string, liked: boolean) {
    const user = this.userId ? { id: this.userId } : await this.ensureSession()
    const query = liked
      ? this.client.from('comment_likes').upsert({ comment_id: commentId, user_id: user.id }, { onConflict: 'comment_id,user_id' })
      : this.client.from('comment_likes').delete().eq('comment_id', commentId).eq('user_id', user.id)
    const { error } = await query
    if (error) throw error
  }
  async recordPlay(app: AppyCreation) {
    if (isSocialPost(app)) return
    if (isUuid(app.id)) { const { error } = await this.client.rpc('record_project_play', { p_project_id: app.id }); if (error) throw error }
  }
  async publish(app: AppyCreation, body?: string) {
    if (isSocialPost(app)) return app.postId ?? null
    if (!isUuid(app.id)) return null
    const caption = (body ?? app.postBody ?? app.description ?? '').trim()
    const { data, error } = await this.client.rpc('publish_project', { p_project_id: app.id, p_body: caption, p_visibility: 'public' })
    if (error) throw error
    return typeof data === 'string' ? data : null
  }
  async deletePost(postId: string) {
    if (!isUuid(postId)) throw new Error('Invalid post')
    const { error } = await this.client.rpc('delete_post', { p_post_id: postId })
    if (error) throw error
  }
  async updatePost(postId: string, body: string) {
    if (!isUuid(postId)) throw new Error('Invalid post')
    const { error } = await this.client.rpc('update_post', { p_post_id: postId, p_body: body })
    if (error) throw error
  }
  async createTextPost(body: string, mediaUrl?: string | null): Promise<AppyCreation> {
    const authUser = await this.ensureSession()
    if (authUser.is_anonymous) throw new Error('Create an account before posting.')
    const { data, error } = await this.client.rpc('create_text_post', { p_body: body, p_media_url: mediaUrl ?? null })
    if (error) throw error
    const postId = String(data)
    const now = new Date().toISOString()
    const hashtags = Array.from(body.matchAll(/#([a-zA-Z][\w]{1,29})/g)).map((match) => match[1])
    const isVideo = Boolean(mediaUrl && (/\.(mp4|webm|mov)(\?|$)/i.test(mediaUrl) || mediaUrl.includes('/short-videos/')))
    return {
      id: postId, postId, postType: 'text', authorId: authUser.id, title: 'Post', description: body.trim(), postBody: body.trim(),
      prompt: '', kind: 'app', status: 'published', createdAt: now, updatedAt: now, publishedAt: now, srcDoc: '', coverClass: 'cover-text',
      mediaUrl: isVideo ? null : (mediaUrl ?? null), videoUrl: isVideo ? mediaUrl : null,
      mediaKind: mediaUrl ? (isVideo ? 'video' : 'image') : 'none',
      tags: hashtags.length > 0 ? hashtags : ['Post'], likes: 0, comments: 0, reposts: 0, saves: 0, plays: 0, version: 1, versions: [],
    }
  }
  async createShortPost(body: string, videoUrl: string, durationSeconds?: number | null): Promise<AppyCreation> {
    const authUser = await this.ensureSession()
    if (authUser.is_anonymous) throw new Error('Create an account before posting Shorts.')
    const { data, error } = await this.client.rpc('create_short_post', {
      p_body: body,
      p_video_url: videoUrl,
      p_duration_seconds: durationSeconds ?? null,
    })
    if (error) throw error
    const postId = String(data)
    const now = new Date().toISOString()
    const caption = body.trim() || 'Short'
    const hashtags = Array.from(caption.matchAll(/#([a-zA-Z][\w]{1,29})/g)).map((match) => match[1])
    return {
      id: postId, postId, postType: 'short', authorId: authUser.id, title: 'Short', description: caption, postBody: caption,
      prompt: '', kind: 'app', status: 'published', createdAt: now, updatedAt: now, publishedAt: now, srcDoc: '', coverClass: 'cover-short',
      mediaUrl: null, videoUrl, mediaKind: 'video', durationSeconds: durationSeconds ?? null,
      tags: hashtags.length > 0 ? hashtags : ['Short'], likes: 0, comments: 0, reposts: 0, saves: 0, plays: 0, version: 1, versions: [],
    }
  }
  async staffBanUser(userId: string, reason?: string) {
    if (!isUuid(userId)) throw new Error('Invalid user')
    const { error } = await this.client.rpc('staff_ban_user', { p_user_id: userId, p_reason: reason ?? '' })
    if (error) throw error
  }
  async staffUnbanUser(userId: string) {
    if (!isUuid(userId)) throw new Error('Invalid user')
    const { error } = await this.client.rpc('staff_unban_user', { p_user_id: userId })
    if (error) throw error
  }
  async staffSetModerator(userId: string, enabled: boolean) {
    if (!isUuid(userId)) throw new Error('Invalid user')
    const { error } = await this.client.rpc('staff_set_moderator', { p_user_id: userId, p_enabled: enabled })
    if (error) throw error
  }
  async requestSubscriberVerification(userId?: string) {
    const result = await this.client.functions.invoke('verify-subscriber', {
      body: userId ? { user_id: userId } : {},
    })
    if (result.error) throw result.error
    return (result.data ?? { status: 'unknown' }) as { status: string }
  }
  async moderatePostImage(file: File, options?: { maxBytes?: number }) {
    const user = await this.ensureSession()
    if (user.is_anonymous) throw new Error('Create an account before posting images.')
    if (!(file instanceof File) || file.size <= 0) throw new Error('That image file is empty. Try another photo.')
    const maxBytes = options?.maxBytes ?? 8 * 1024 * 1024
    if (file.size > maxBytes) throw new Error(`Images must be ${Math.round(maxBytes / (1024 * 1024))}MB or smaller on your plan.`)

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ''))
      reader.onerror = () => reject(new Error('Could not read that image for safety review.'))
      reader.readAsDataURL(file)
    })
    const comma = dataUrl.indexOf(',')
    const imageBase64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl
    const mediaType = (file.type && file.type.startsWith('image/') ? file.type : 'image/jpeg').toLowerCase()

    const result = await this.client.functions.invoke('moderate-image', {
      body: { image_base64: imageBase64, media_type: mediaType },
    })
    const payload = (result.data ?? {}) as { allowed?: boolean; sexual_content?: boolean; message?: string; error?: { message?: string } }
    if (payload.allowed === false || payload.sexual_content === true) {
      throw new Error(payload.message || 'Post could not be posted due to the sexual content.')
    }
    if (result.error) {
      const context = result.error as { context?: Response; message?: string }
      try {
        const body = context.context ? await context.context.json() as { message?: string; error?: { message?: string }; sexual_content?: boolean } : null
        if (body?.sexual_content || /sexual/i.test(body?.message ?? body?.error?.message ?? '')) {
          throw new Error(body?.message || body?.error?.message || 'Post could not be posted due to the sexual content.')
        }
        if (body?.error?.message) throw new Error(body.error.message)
        if (body?.message) throw new Error(body.message)
      } catch (inner) {
        if (inner instanceof Error && /sexual|could not be posted/i.test(inner.message)) throw inner
      }
      throw new Error(result.error.message || 'Could not review this image.')
    }
  }

  /** Sample frames and send to gpt-5.6-luna via moderate-video (or moderate-image fallback). */
  async moderatePostVideo(file: File, options?: { maxBytes?: number; maxSeconds?: number }) {
    const user = await this.ensureSession()
    if (user.is_anonymous) throw new Error('Create an account before posting videos.')
    if (!(file instanceof File) || file.size <= 0) throw new Error('That video file is empty. Try another clip.')
    const maxBytes = options?.maxBytes ?? 40 * 1024 * 1024
    if (file.size > maxBytes) throw new Error(`Videos must be ${Math.round(maxBytes / (1024 * 1024))}MB or smaller on your plan.`)
    const type = (file.type || '').toLowerCase()
    if (type && !type.startsWith('video/')) throw new Error('Use an MP4, WEBM, or MOV video.')

    const frames = await extractVideoFrames(file, 3, options?.maxSeconds)
    if (frames.length === 0) throw new Error('Could not read frames from that video for safety review.')

    const result = await this.client.functions.invoke('moderate-video', {
      body: {
        frames: frames.map((frame) => ({ image_base64: frame.base64, media_type: frame.mediaType })),
        duration_seconds: frames[0]?.durationSeconds ?? null,
      },
    })
    const payload = (result.data ?? {}) as { allowed?: boolean; sexual_content?: boolean; message?: string }
    if (payload.allowed === false || payload.sexual_content === true) {
      throw new Error(payload.message || 'Short could not be posted due to the sexual content.')
    }
    if (result.error) {
      // Fallback: moderate first frame via image pipeline if video function is not deployed yet
      try {
        await this.moderatePostImage(
          new File([Uint8Array.from(atob(frames[0].base64), (c) => c.charCodeAt(0))], 'frame.jpg', { type: frames[0].mediaType }),
          { maxBytes: 8 * 1024 * 1024 },
        )
        return
      } catch (inner) {
        if (inner instanceof Error && /sexual|could not be posted/i.test(inner.message)) throw inner
      }
      const context = result.error as { context?: Response; message?: string }
      try {
        const body = context.context ? await context.context.json() as { message?: string; error?: { message?: string }; sexual_content?: boolean } : null
        if (body?.sexual_content) throw new Error(body.message || 'Short could not be posted due to the sexual content.')
        if (body?.error?.message) throw new Error(body.error.message)
        if (body?.message) throw new Error(body.message)
      } catch (inner) {
        if (inner instanceof Error && /sexual|could not be posted/i.test(inner.message)) throw inner
      }
      throw new Error(result.error.message || 'Could not review this video.')
    }
  }
  async reportContent(input: { reason: string; details?: string; postId?: string; projectId?: string; reportedUserId?: string }) {
    const { error } = await this.client.rpc('report_content', {
      p_reason: input.reason,
      p_details: input.details ?? '',
      p_post_id: input.postId ?? null,
      p_project_id: input.projectId ?? null,
      p_reported_user_id: input.reportedUserId ?? null,
    })
    if (error) throw error
  }
  private staffError(error: { message?: string } | null, fallback: string) {
    return new Error(error?.message?.trim() || fallback)
  }
  async staffAdminStats() {
    const { data, error } = await this.client.rpc('staff_admin_stats')
    if (error) throw this.staffError(error, 'Could not load admin data.')
    return (data ?? {}) as AdminStats
  }
  async staffListBannedUsers() {
    const { data, error } = await this.client.rpc('staff_list_banned_users')
    if (error) throw this.staffError(error, 'Could not load ban list.')
    return (data ?? []) as AdminBannedUser[]
  }
  async staffListReports(status?: string | null) {
    const { data, error } = await this.client.rpc('staff_list_reports', { p_status: status ?? null })
    if (error) throw this.staffError(error, 'Could not load reports.')
    return (data ?? []) as AdminReport[]
  }
  async staffUpdateReport(reportId: string, status: AdminReportStatus, note?: string) {
    if (!isUuid(reportId)) throw new Error('Invalid report')
    const { error } = await this.client.rpc('staff_update_report', {
      p_report_id: reportId,
      p_status: status,
      p_resolution_note: note ?? '',
    })
    if (error) throw this.staffError(error, 'Could not update report.')
  }
  async staffListRecentPosts(limit = 50) {
    const { data, error } = await this.client.rpc('staff_list_recent_posts', { p_limit: limit })
    if (error) throw this.staffError(error, 'Could not load posts.')
    return (data ?? []) as AdminPostRow[]
  }
  async staffMassDeletePosts(postIds: string[]) {
    const ids = postIds.filter(isUuid)
    if (ids.length === 0) return 0
    const { data, error } = await this.client.rpc('staff_mass_delete_posts', { p_post_ids: ids })
    if (error) throw this.staffError(error, 'Could not delete posts.')
    return Number(data ?? 0)
  }
  async setProjectCover(projectId: string, coverUrl: string | null) {
    if (!isUuid(projectId)) throw new Error('Invalid project')
    const { error } = await this.client.rpc('set_project_cover', { p_project_id: projectId, p_cover_url: coverUrl })
    if (error) throw error
  }
  async uploadMedia(kind: 'avatar' | 'banner' | 'cover' | 'post' | 'short', file: File, projectId?: string, options?: { maxBytes?: number }) {
    const user = await this.ensureSession()
    if (user.is_anonymous) throw new Error('Create an account before uploading media.')
    if (!(file instanceof File) || file.size <= 0) throw new Error('That file is empty. Try another.')
    const isVideo = kind === 'short' || (file.type || '').startsWith('video/')
    const defaultMax = isVideo ? 40 * 1024 * 1024 : 8 * 1024 * 1024
    const maxBytes = options?.maxBytes ?? defaultMax
    if (file.size > maxBytes) {
      throw new Error(`${isVideo ? 'Videos' : 'Images'} must be ${Math.round(maxBytes / (1024 * 1024))}MB or smaller on your plan.`)
    }

    const rawExt = (file.name.split('.').pop() || '').toLowerCase().replace(/[^a-z0-9]/g, '')
    if (isVideo) {
      const mimeFromExt: Record<string, string> = {
        mp4: 'video/mp4', webm: 'video/webm', mov: 'video/quicktime', qt: 'video/quicktime',
      }
      const contentType = (file.type && file.type.startsWith('video/') ? file.type : mimeFromExt[rawExt] || 'video/mp4').toLowerCase()
      if (!['video/mp4', 'video/webm', 'video/quicktime'].includes(contentType)) {
        throw new Error('Use an MP4, WEBM, or MOV video.')
      }
      const ext = contentType === 'video/quicktime' ? 'mov' : contentType === 'video/webm' ? 'webm' : 'mp4'
      const bucket = 'short-videos'
      const path = `${user.id}/short-${crypto.randomUUID()}.${ext}`
      const bytes = await file.arrayBuffer()
      if (bytes.byteLength <= 0) throw new Error('That video file is empty. Try another clip.')
      const blob = new Blob([bytes], { type: contentType })
      const storage = this.client.storage.from(bucket)
      let { error } = await storage.upload(path, blob, { upsert: true, contentType, cacheControl: '3600' })
      if (error && /exists|duplicate|already|conflict|no content|bucket/i.test(error.message)) {
        if (/bucket|not found/i.test(error.message)) {
          throw new Error('Shorts storage is not ready yet. Apply the shorts migration, then try again.')
        }
        await storage.remove([path])
        ;({ error } = await storage.upload(path, blob, { upsert: false, contentType, cacheControl: '3600' }))
      }
      if (error) throw new Error(error.message || 'Could not upload that video.')
      const { data } = storage.getPublicUrl(path)
      if (!data.publicUrl) throw new Error('Upload finished, but Appy could not build a public video URL.')
      return `${data.publicUrl}?v=${Date.now()}`
    }

    const mimeFromExt: Record<string, string> = {
      jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', gif: 'image/gif',
    }
    const contentType = (file.type && file.type.startsWith('image/') ? file.type : mimeFromExt[rawExt] || '').toLowerCase()
    if (!contentType || !['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(contentType)) {
      throw new Error('Use a JPG, PNG, WEBP, or GIF image.')
    }
    const ext = contentType === 'image/jpeg' ? 'jpg' : contentType.split('/')[1] || 'jpg'
    const bucket = kind === 'avatar' ? 'avatars' : kind === 'banner' ? 'banners' : kind === 'cover' ? 'covers' : 'post-media'
    const path = kind === 'cover' && projectId
      ? `${user.id}/${projectId}.${ext}`
      : `${user.id}/${kind}-${crypto.randomUUID()}.${ext}`

    // Always send a concrete Blob body. Passing a raw File stream can produce
    // Supabase Storage "No content provided" on some browsers/sessions.
    const bytes = await file.arrayBuffer()
    if (bytes.byteLength <= 0) throw new Error('That image file is empty. Try another photo.')
    const blob = new Blob([bytes], { type: contentType })

    const storage = this.client.storage.from(bucket)
    let { error } = await storage.upload(path, blob, {
      upsert: true,
      contentType,
      cacheControl: '3600',
    })
    if (error && /exists|duplicate|already|conflict|no content/i.test(error.message)) {
      await storage.remove([path])
      ;({ error } = await storage.upload(path, blob, {
        upsert: false,
        contentType,
        cacheControl: '3600',
      }))
    }
    if (error) throw new Error(error.message || 'Could not upload that image.')
    const { data } = storage.getPublicUrl(path)
    if (!data.publicUrl) throw new Error('Upload finished, but Appy could not build a public image URL.')
    return `${data.publicUrl}?v=${Date.now()}`
  }
  async restoreVersion(app: AppyCreation, version: AppVersion): Promise<VersionRestoreResult> {
    if (!isUuid(app.id) || !version.id || !isUuid(version.id)) throw new Error('This version cannot be restored from the server.')
    const { data, error } = await this.client.rpc('restore_project_version', {
      p_project_id: app.id,
      p_version_id: version.id,
    })
    if (error) throw error
    const payload = (data ?? {}) as Row
    const rows = await this.loadProjectVersions([app.id])
    const versions = rows.map(toAppVersion).sort((a, b) => a.number - b.number)
    const restoredId = String(payload.version_id ?? '')
    const restoredNumber = Number(payload.version_number ?? 0)
    const current = versions.find((item) => item.id === restoredId)
      ?? versions.find((item) => item.number === restoredNumber)
    if (!current) throw new Error('The version was restored, but Appy could not refresh its contents.')
    const rpcStatus = String(payload.status ?? '')
    const status = app.status === 'published'
      ? 'published'
      : rpcStatus === 'draft' ? 'ready' : rpcStatus as AppyCreation['status']
    return { current, versions, status }
  }
  async markNotificationsRead(ids?: string[]) { const { error } = await this.client.rpc('mark_notifications_read', { p_notification_ids: ids ?? null }); if (error) throw error }

  async setDailySchedule(schedule: DailySchedule): Promise<void> {
    const user = this.userId ? { id: this.userId } : await this.ensureSession()
    const nextRun = new Date()
    nextRun.setDate(nextRun.getDate() + 1)
    nextRun.setHours(schedule.hour, 0, 0, 0)
    const { error } = await this.client.from('daily_schedules').upsert({
      user_id: user.id,
      enabled: schedule.enabled,
      kind: 'game',
      intelligence: schedule.intelligence,
      prompt_template: 'Create a polished, original surprise game that is fun, complete, and easy to start playing.',
      auto_publish: true,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      local_hour: schedule.hour,
      next_run_at: schedule.enabled ? nextRun.toISOString() : null,
    }, { onConflict: 'user_id' })
    if (error) throw error
  }

  private async invokeRuntime(projectId: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
    await this.ensureSession()
    const result = await this.client.functions.invoke('runtime-storage', { body: { project_id: projectId, ...body } })
    if (result.error) throw result.error
    return (result.data ?? {}) as Record<string, unknown>
  }

  async runtimeGet(projectId: string, key: string): Promise<unknown> {
    if (!isUuid(projectId)) return localRuntimeGet(projectId, key)
    const data = await this.invokeRuntime(projectId, { action: 'get', key })
    return data.value ?? null
  }

  async runtimeSet(projectId: string, key: string, value: unknown): Promise<void> {
    if (!isUuid(projectId)) { localRuntimeSet(projectId, key, value); return }
    await this.invokeRuntime(projectId, { action: 'set', key, value })
  }

  async runtimeDelete(projectId: string, key: string): Promise<void> {
    if (!isUuid(projectId)) { localRuntimeDelete(projectId, key); return }
    await this.invokeRuntime(projectId, { action: 'delete', key })
  }

  async runtimePublicProfile(projectId: string): Promise<unknown> {
    if (!isUuid(projectId)) return this.publicProfile
    const data = await this.invokeRuntime(projectId, { action: 'public_profile' })
    return data.value ?? null
  }

  async purchasePlan(plan: 'creator' | 'pro') {
    await this.ensureFreshSession()
    const result = await this.client.functions.invoke('stripe-checkout', { body: { kind: 'subscription', plan } })
    if (result.error || !result.data?.url) throw await this.functionError(result, 'Could not open secure checkout.')
    return String(result.data.url)
  }

  async purchaseCredits(amount: number) {
    await this.ensureFreshSession()
    const result = await this.client.functions.invoke('stripe-checkout', { body: { kind: 'topup', credit_amount: amount } })
    if (result.error || !result.data?.url) throw await this.functionError(result, 'Could not open secure checkout.')
    return String(result.data.url)
  }

  async openBillingPortal() {
    await this.ensureFreshSession()
    const result = await this.client.functions.invoke('customer-portal', { body: {} })
    if (result.error || !result.data?.url) throw await this.functionError(result, 'Could not open billing management.')
    return String(result.data.url)
  }

  private authError(error: { message?: string; code?: string; status?: number } | null | undefined, fallback: string) {
    const message = error?.message ?? fallback
    const code = error?.code ?? ''
    if (/rate limit|over_email_send_rate_limit|429/i.test(`${code} ${message}`)) {
      return new Error(
        'Sign-up is temporarily rate-limited (too many attempts from this network). Wait a few minutes and try again, or use Sign in if you already created the account.',
      )
    }
    if (/already registered|already been registered|user already exists|User already registered/i.test(message)) {
      return new Error('That email already has an Appy account. Use Sign in instead.')
    }
    if (/Email not confirmed|email_not_confirmed/i.test(`${code} ${message}`)) {
      return new Error('Confirm your email from the link we sent, then try signing in again.')
    }
    return new Error(message || fallback)
  }

  async secureAccount(email: string, password: string) {
    // Prefer a fresh email sign-up. Linking email onto an anonymous session via
    // updateUser counts as an email change and can hit Supabase’s free mailer
    // rate limit (2 emails/hour) even on the first attempt.
    return this.signUp(email, password)
  }

  async signUp(email: string, password: string) {
    const normalizedEmail = email.trim().toLowerCase()
    const current = await this.client.auth.getSession()
    if (current.data.session?.user && !current.data.session.user.is_anonymous) {
      throw new Error('You are already signed in. Sign out before creating a new account.')
    }
    // Drop the anonymous guest session first so we create a real email user
    // instead of triggering an email-change flow (extra mailer rate-limit hits).
    if (current.data.session?.user?.is_anonymous) {
      await this.client.auth.signOut({ scope: 'local' })
      this.userId = null
      this.publicProfile = null
    }
    const { data, error } = await this.client.auth.signUp({
      email: normalizedEmail,
      password,
      options: { emailRedirectTo: `${window.location.origin}/?account=secured` },
    })
    if (error) {
      // If the account was created earlier but the UI showed a rate-limit error,
      // try signing in so the user isn't stuck.
      if (/rate limit|over_email_send_rate_limit|429|already registered|already been registered/i.test(`${error.code ?? ''} ${error.message ?? ''}`)) {
        const signedIn = await this.client.auth.signInWithPassword({ email: normalizedEmail, password })
        if (!signedIn.error && signedIn.data.user) {
          this.userId = signedIn.data.user.id
          return {
            email: signedIn.data.user.email ?? normalizedEmail,
            verificationRequired: !signedIn.data.user.email_confirmed_at,
          }
        }
      }
      throw this.authError(error, 'Could not create this account.')
    }
    if (!data.user) throw new Error('Appy could not create that account.')
    // Autoconfirm returns a session immediately. Without it, sign in once so
    // the client can keep working when the mailbox is slow.
    if (!data.session) {
      const signedIn = await this.client.auth.signInWithPassword({ email: normalizedEmail, password })
      if (signedIn.error || !signedIn.data.user) {
        this.userId = data.user.id
        return { email: data.user.email ?? normalizedEmail, verificationRequired: !data.user.email_confirmed_at }
      }
      this.userId = signedIn.data.user.id
      return {
        email: signedIn.data.user.email ?? normalizedEmail,
        verificationRequired: !signedIn.data.user.email_confirmed_at,
      }
    }
    this.userId = data.user.id
    return { email: data.user.email ?? normalizedEmail, verificationRequired: !data.user.email_confirmed_at }
  }

  async signIn(email: string, password: string): Promise<{ mfaRequired: boolean }> {
    const normalizedEmail = email.trim().toLowerCase()
    const existing = await this.client.auth.getSession()
    if (existing.data.session?.user?.is_anonymous) {
      await this.client.auth.signOut({ scope: 'local' })
      this.userId = null
      this.publicProfile = null
    }
    const { data, error } = await this.client.auth.signInWithPassword({ email: normalizedEmail, password })
    if (error || !data.user) throw this.authError(error, 'Appy could not sign in to that account.')
    this.userId = data.user.id
    return { mfaRequired: await this.mfaLoginRequired() }
  }

  async mfaLoginRequired(): Promise<boolean> {
    const { data, error } = await this.client.auth.mfa.getAuthenticatorAssuranceLevel()
    if (error || !data) return false
    return data.nextLevel === 'aal2' && data.currentLevel !== 'aal2'
  }

  async verifyMfaLogin(code: string): Promise<void> {
    const trimmed = code.trim().replace(/\s+/g, '')
    if (!/^\d{6}$/.test(trimmed)) throw new Error('Enter the 6-digit code from your authenticator app.')
    const factors = await this.client.auth.mfa.listFactors()
    if (factors.error) throw factors.error
    const factor = (factors.data?.totp ?? []).find((item) => item.status === 'verified')
    if (!factor) throw new Error('No authenticator is enrolled on this account. Enable 2FA in Settings first.')
    const challenge = await this.client.auth.mfa.challenge({ factorId: factor.id })
    if (challenge.error) throw challenge.error
    const verified = await this.client.auth.mfa.verify({
      factorId: factor.id,
      challengeId: challenge.data.id,
      code: trimmed,
    })
    if (verified.error) throw this.authError(verified.error, 'That authenticator code was not accepted.')
  }

  async signOut(): Promise<void> {
    const { error } = await this.client.auth.signOut({ scope: 'local' })
    if (error) throw error
    this.userId = null
    this.publicProfile = null
  }

  async resetPassword(email: string): Promise<void> {
    const { error } = await this.client.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
      redirectTo: `${window.location.origin}/?account=password-reset`,
    })
    if (error) throw this.authError(error, 'Could not send a password reset email.')
  }

  async updatePassword(password: string): Promise<void> {
    const { error } = await this.client.auth.updateUser({ password })
    if (error) throw this.authError(error, 'Could not update your password.')
  }

  async listMfaFactors() {
    const { data, error } = await this.client.auth.mfa.listFactors()
    if (error) throw error
    return (data.totp ?? []).map((factor) => ({
      id: factor.id,
      friendlyName: factor.friendly_name,
      status: factor.status,
    }))
  }

  async enrollMfa() {
    const { data, error } = await this.client.auth.mfa.enroll({
      factorType: 'totp',
      friendlyName: 'Appy authenticator',
    })
    if (error) throw error
    return {
      id: data.id,
      qrCode: data.totp.qr_code,
      secret: data.totp.secret,
      uri: data.totp.uri,
    }
  }

  async verifyMfaEnrollment(factorId: string, code: string) {
    const challenge = await this.client.auth.mfa.challenge({ factorId })
    if (challenge.error) throw challenge.error
    const verified = await this.client.auth.mfa.verify({
      factorId,
      challengeId: challenge.data.id,
      code: code.trim(),
    })
    if (verified.error) throw verified.error
  }

  async unenrollMfa(factorId: string) {
    const { error } = await this.client.auth.mfa.unenroll({ factorId })
    if (error) throw error
  }
}
